import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import EmailInput from '../components/EmailInput';
import PasswordInput from '../components/PasswordInput';
import { AuthService } from '../services/auth';
import { SwatchBook, Check } from 'lucide-react';

const authService = new AuthService();

const SignupPromotionCard = () => {
  // Subtle fade-in animation for each benefit
  const [visible, setVisible] = useState(false);
  
  useEffect(() => {
    // Add a delay before starting the animation
    const timer = setTimeout(() => {
      setVisible(true);
    }, 250); // 1 second delay
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-lg shadow-sm mb-6 border border-orange-200 transition-all duration-500 ease-in-out hover:shadow-md">
      <div>
        <h2 className="font-bold text-gray-800 mb-3">
          Create beautiful, interactive designs
        </h2>
        
        <div className="flex flex-col space-y-2 text-sm">
          {[
            "Full access to all features",
            "10 free AI-generations",
            "No credit card required"
          ].map((benefit, index) => (
            <div 
              key={index} 
              className={`flex items-center transition-opacity duration-500 ease-in-out ${visible ? 'opacity-100' : 'opacity-0'}`} 
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              <Check className="h-4 w-4 text-orange-500 mr-2" />
              <span className="text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Delay the form fade-in animation
    const timer = setTimeout(() => {
      setFormVisible(true);
    }, 250); // 1 second delay
    
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeTerms) {
      setError('You must agree with the Terms of Service and Privacy Policy.');
      return;
    }
  
    try {
      setIsSubmitting(true);
      const signUpResponse = await authService.signUp(email, password);
      console.log("UserId from AWS:", signUpResponse.userId);
  
      // Navigate to verification, passing the email and AWS user id
      navigate('/verify-email', { 
        state: { 
          email,
          userId: signUpResponse.userId 
        } 
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto px-4">
{/* Logo and brand name with subtle animation - modified to stack vertically */}
<Link 
  to="/" 
  className="flex flex-col items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-105"
>
  <SwatchBook className="w-12 h-12 text-orange-500 mb-2" />
  <span className="text-2xl font-bold text-black">dessino.ai</span>
</Link>
        
        <SignupPromotionCard />
        
        <div 
          className={`bg-white p-6 rounded-lg shadow-sm transition-all duration-500 ease-in-out ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <h1 className="text-2xl font-bold mb-5">Sign Up</h1>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 animate-pulse">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <EmailInput value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="terms"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="w-4 h-4 mr-2 border-gray-300 rounded"
                required
              />
              <label htmlFor="terms" className="text-gray-700 text-sm">
                I agree with the <Link to="/terms" className="text-blue-500 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>
              </label>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-black text-white py-3 rounded-md hover:bg-gray-800 transition-all duration-300 flex items-center justify-center transform hover:scale-[1.01] active:scale-[0.99]"
            >
              {isSubmitting ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></span>
              ) : null}
              {isSubmitting ? "Signing Up..." : "Sign Up"}
            </button>
          </form>
          
          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;